import { AccountPicker } from "@/components/accounting/account-picker";
import {
	type DimensionFilter,
	DimensionFilterBar,
} from "@/components/accounting/dimension-filter-bar";
import { DrillDownDialog } from "@/components/accounting/drill-down-dialog";
import { MoneyDisplay } from "@/components/accounting/money-display";
import { Button } from "@/components/ui/button";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { AccountAnalysisLine } from "@/gen/omnios/v1/accounting_pb";
import { useGetAccountAnalysis, useListAccounts } from "@/hooks/use-accounting";
import { useAccountingContext } from "@/lib/accounting-context";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface AnalysisSearch {
	account?: string;
}

export const Route = createFileRoute("/accounting/analysis")({
	component: AccountAnalysisPage,
	validateSearch: (search: Record<string, unknown>): AnalysisSearch => ({
		account: typeof search.account === "string" ? search.account : undefined,
	}),
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function AccountAnalysisPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const search = Route.useSearch();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("analysis");
	const { periodId, basis } = useAccountingContext();

	useBreadcrumbs([
		{ label: t("account_analysis", { defaultValue: "Анализ счёта" }) },
	]);

	const [accountCode, setAccountCode] = useState(search.account ?? "");
	const [dimensionsFilter, setDimensionsFilter] = useState<DimensionFilter>({});

	const { data: accountsData } = useListAccounts();
	const accounts = useMemo(
		() =>
			(accountsData?.accounts ?? []).map((a) => ({
				id: a.id,
				code: a.code,
				name: a.name,
				accountType: a.accountType,
			})),
		[accountsData],
	);

	const { data: analysis, isLoading } = useGetAccountAnalysis(
		accountCode,
		periodId,
		basis,
	);

	// Drill-down state
	const [drillDown, setDrillDown] = useState<{
		accountCode: string;
		title: string;
	} | null>(null);

	const columnDefs = useMemo<ColDef<AccountAnalysisLine>[]>(
		() => [
			{
				field: "offsetAccountCode",
				headerName: t("offset_account", { defaultValue: "Корр. счёт" }),
				width: 100,
				pinned: "left" as const,
				cellClass: "font-mono text-primary cursor-pointer hover:underline",
			},
			{
				field: "offsetAccountName",
				headerName: t("account_name"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "debitTurnover",
				headerName: t("line_debit"),
				width: 150,
				type: "rightAligned",
				cellClass: "font-tabular cursor-pointer hover:underline",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.debitTurnover || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
			{
				field: "creditTurnover",
				headerName: t("line_credit"),
				width: 150,
				type: "rightAligned",
				cellClass: "font-tabular cursor-pointer hover:underline",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.creditTurnover || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const pinnedBottomRowData = useMemo(() => {
		if (!analysis?.lines.length) return [];
		return [
			{
				offsetAccountCode: t("total"),
				offsetAccountName: "",
				debitTurnover: analysis.totalDebit,
				creditTurnover: analysis.totalCredit,
			} as unknown as AccountAnalysisLine,
		];
	}, [analysis, t]);

	const onCellClicked = useCallback(
		(e: {
			colDef: ColDef;
			data: AccountAnalysisLine | undefined;
		}) => {
			if (!e.data || e.data.offsetAccountCode === t("total")) return;

			if (e.colDef.field === "offsetAccountCode") {
				// Navigate to analysis of the offset account
				navigate({
					to: "/accounting/analysis",
					search: { account: e.data.offsetAccountCode },
				});
				setAccountCode(e.data.offsetAccountCode);
				return;
			}

			// Click on amount → drill-down
			if (
				e.colDef.field === "debitTurnover" ||
				e.colDef.field === "creditTurnover"
			) {
				setDrillDown({
					accountCode,
					title: `${accountCode} ↔ ${e.data.offsetAccountCode} ${e.data.offsetAccountName}`,
				});
			}
		},
		[accountCode, navigate, t],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			...(accountCode
				? [
						{
							id: "account",
							content: `${t("account_code")}: ${accountCode} ${analysis?.accountName ?? ""}`,
							align: "left" as const,
						},
					]
				: []),
			{
				id: "count",
				content: t("row_count", {
					count: analysis?.lines.length ?? 0,
				}),
				align: "left" as const,
			},
		],
		[accountCode, analysis, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* Drill-down dialog */}
			<DrillDownDialog
				open={!!drillDown}
				onOpenChange={(open) => {
					if (!open) setDrillDown(null);
				}}
				accountCode={drillDown?.accountCode}
				periodId={periodId}
				title={drillDown?.title}
			/>

			{/* Controls + dimension filter bar */}
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<AccountPicker
					value={accountCode}
					onChange={(code) => {
						setAccountCode(code);
						navigate({
							to: "/accounting/analysis",
							search: { account: code },
							replace: true,
						});
					}}
					accounts={accounts}
					className="h-6 w-[280px] text-xs"
				/>
				<div className="h-4 w-px bg-border" />
				<DimensionFilterBar
					value={dimensionsFilter}
					onChange={setDimensionsFilter}
					inline
				/>
				<div className="flex-1" />
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `analysis_${accountCode}_${periodId}.csv`,
						})
					}
				>
					CSV
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => window.print()}
				>
					{t("toolbar_print")}
				</Button>
			</div>

			{/* Balance summary */}
			{analysis && (
				<div className="grid grid-cols-3 gap-px bg-border border-b shrink-0">
					<div className="bg-background px-3 py-1.5">
						<p className="text-[10px] text-muted-foreground">
							{t("opening_balance")}
						</p>
						<MoneyDisplay
							amount={analysis.openingBalance}
							className="text-sm font-semibold"
						/>
					</div>
					<div className="bg-background px-3 py-1.5">
						<p className="text-[10px] text-muted-foreground">{t("turnover")}</p>
						<span className="text-sm font-semibold tabular-nums">
							{t("line_debit")}:{" "}
							{formatAmount(Number.parseFloat(analysis.totalDebit || "0"))}
							{" / "}
							{t("line_credit")}:{" "}
							{formatAmount(Number.parseFloat(analysis.totalCredit || "0"))}
						</span>
					</div>
					<div className="bg-background px-3 py-1.5">
						<p className="text-[10px] text-muted-foreground">
							{t("closing_balance")}
						</p>
						<MoneyDisplay
							amount={analysis.closingBalance}
							className="text-sm font-semibold"
							colorize
						/>
					</div>
				</div>
			)}

			{/* Grid */}
			{!accountCode ? (
				<div className="flex-1 flex items-center justify-center">
					<p className="text-sm text-muted-foreground">
						{t("select_account_for_analysis", {
							defaultValue: "Выберите счёт для анализа",
						})}
					</p>
				</div>
			) : (
				<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
					<AgGridReact<AccountAnalysisLine>
						ref={gridRef}
						rowData={analysis?.lines ?? []}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						onGridReady={onGridReady}
						onSortChanged={onStateChanged}
						onFilterChanged={onStateChanged}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						rowHeight={28}
						headerHeight={32}
						animateRows={false}
						getRowId={(p) => p.data.offsetAccountCode}
						loading={isLoading}
						pinnedBottomRowData={pinnedBottomRowData}
						onCellClicked={onCellClicked}
						overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_analysis_title", { defaultValue: "Нет оборотов" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_analysis_desc", { defaultValue: "По этому счёту нет проводок за выбранный период" })}</p></div>`}
					/>
				</div>
			)}
		</div>
	);
}
